import type { SignatureField } from '@/modules/templates/components/review/SignatureFieldOverlay';
import type { ContractSignatory } from '@/modules/org/contracts/types/contract-document.types';
import { FORCED_ANCHOR_HEIGHT_PT } from '@/lib/pdf/detect-signature-anchors';
import {
  DATE_H,
  DATE_W,
  GAP,
  SIG_H,
  SIG_W,
  dateFieldId,
  defaultFieldPair,
  sigFieldId,
} from './signature-fields';
import { createAnchorResolver, type AnchorRect, type AnchorTier } from './anchor-locate';
import type { DocxPageGeometry } from './useDocxPageGeometry';

/**
 * How far to lift an anchor-derived box above the anchor text, as a fraction of
 * the box height.
 *
 * The anchor rect is the *text* of the signing line, so a box hung from its top
 * covers the line and everything under it. Lifting sits the box mostly above
 * the line, which is where a signature belongs — past half, so the bulk of the
 * box clears the text rather than straddling it.
 *
 * Preview-only, and deliberately not shared with `detectSignatureLocations`:
 * the send path measures LibreOffice's PDF, and its boxes are positioned by
 * that flow's own rules. Changing this shifts what the user sees while placing,
 * not what gets signed.
 */
const PREVIEW_ANCHOR_LIFT_RATIO = 0.65;

/** Where an anchor was found, for the diagnostic overlay. Viewport coordinates. */
export interface SeededAnchor {
  signatoryIndex: number;
  label: string;
  tier: AnchorTier;
  rect: AnchorRect;
}

/** A signatory whose box had to be guessed, and why. */
export interface UnanchoredSignatory {
  signatoryIndex: number;
  label: string;
  reason: 'no-anchor' | 'not-located';
}

export interface SeedResult {
  fields: SignatureField[];
  /**
   * The anchor rects the boxes were derived from. Rendering these over the
   * document separates "found the wrong text" from "placed the box wrongly" —
   * two failures that look identical on screen.
   */
  anchors: SeededAnchor[];
  /**
   * Signatories placed by fallback rather than by anchor. Their boxes sit at an
   * arbitrary spot on the last page, so the review must say so — silently
   * dropping a box in the wrong place reads as a bug rather than as a template
   * that never marked where this person signs.
   */
  unanchored: UnanchoredSignatory[];
}

/**
 * Report how a signatory's box was placed.
 *
 * `routine` marks the outcome we expect — the stored paragraph index resolved
 * cleanly, or the signatory simply has no anchor to place against. Those stay
 * quiet in production and are visible only while developing. Anything else
 * means the box may be in the wrong place, which is worth a warning wherever it
 * happens: a degraded tier, a clamp against the page edge, or an anchor we
 * couldn't find at all.
 */
function trace(routine: boolean, label: string, detail: Record<string, unknown>): void {
  if (!routine) {
    console.warn(label, detail);
    return;
  }
  // debug rather than warn: the browser filters it out by default, so a routine
  // placement leaves no trace unless you go looking for it.
  // eslint-disable-next-line no-console -- deliberate dev-only placement trace
  if (import.meta.env.DEV) console.debug(label, detail);
}

/**
 * Initial signature-field placement over a rendered .docx.
 *
 * Per signatory, in order of preference:
 *   1. a location the user already placed and we persisted,
 *   2. the template's signature marker, located in the rendered document,
 *   3. a default box on the last page.
 *
 * Every signatory ends up with a visible, draggable box — previously an
 * un-anchored one silently got a box only at send time, so the user never saw
 * or had a chance to move it.
 *
 * Anchor-derived boxes are shaped like the ones the send path builds
 * (`detectSignatureLocations`): x from the anchor's left, width from its own
 * extent, height from the shared `FORCED_ANCHOR_HEIGHT_PT`. Only y differs —
 * see `PREVIEW_ANCHOR_LIFT_RATIO`, which centres the box on the signing line
 * rather than hanging it below. The numbers were never going to match to the
 * point anyway (review measures docx-preview's layout, the send path measures
 * LibreOffice's); what matters is that the user sees a real signature box to
 * drag, not a differently-shaped stand-in.
 */
export function seedDocxFields({
  signatories,
  pages,
  root,
  container,
  docxBytes,
}: {
  signatories: ContractSignatory[];
  pages: DocxPageGeometry[];
  /** The docx-preview output root. */
  root: HTMLElement | null;
  /** The positioned element `pages[].left/top` are measured against. */
  container: HTMLElement | null;
  /** Source bytes, for the anchor resolver's XML fallback tier. */
  docxBytes?: Uint8Array | null;
}): SeedResult {
  if (signatories.length === 0 || pages.length === 0)
    return { fields: [], anchors: [], unanchored: [] };
  const containerRect = container?.getBoundingClientRect() ?? null;

  const lastPage = pages[pages.length - 1];
  // One resolver for the whole pass — it shares the paragraph list and defers
  // its expensive models until a signatory actually needs them.
  const resolveAnchor = root ? createAnchorResolver(root, docxBytes) : null;
  const sections = root ? Array.from(root.querySelectorAll<HTMLElement>('section.docxpv')) : [];

  const pageForElement = (node: Node): DocxPageGeometry | null => {
    const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement);
    const section = el?.closest<HTMLElement>('section.docxpv');
    if (!section) return null;
    const index = sections.indexOf(section);
    return index >= 0 ? (pages[index] ?? null) : null;
  };

  const fields: SignatureField[] = [];
  const anchors: SeededAnchor[] = [];
  const unanchored: UnanchoredSignatory[] = [];

  signatories.forEach((signatory, index) => {
    // 1. A placement the user made previously, in THIS preview's coordinate
    //    space. A stored location from send-time anchor detection is deliberately
    //    NOT reused: it was measured on the converted PDF, which paginates
    //    independently of docx-preview, so replaying it here lands the box near
    //    the right text but often on the wrong page. Those re-resolve below.
    const stored =
      signatory.signatureLocationSource === 'user' ? signatory.signatureLocation : null;
    if (stored) {
      const page = pages.find((p) => p.pageNumber === stored.pageNumber) ?? pages[0];
      const date = signatory.dateLocation;

      fields.push({
        id: sigFieldId(index),
        type: 'signature',
        signatoryIndex: index,
        pageNumber: page.pageNumber,
        x: stored.x,
        y: stored.y,
        width: stored.width || SIG_W,
        height: stored.height || SIG_H,
      });
      fields.push({
        id: dateFieldId(index),
        type: 'date',
        signatoryIndex: index,
        pageNumber: date?.pageNumber ?? page.pageNumber,
        x: date?.x ?? stored.x + (stored.width || SIG_W) + GAP,
        y: date?.y ?? stored.y,
        width: date?.width ?? DATE_W,
        height: date?.height ?? DATE_H,
      });
      return;
    }

    // 2. The signature marker, located in the rendered document.
    const anchor = signatory.signatureAnchor;
    const hit = resolveAnchor && anchor?.originalText ? resolveAnchor(anchor) : null;
    const page = hit ? pageForElement(hit.range.startContainer) : null;

    if (hit) {
      anchors.push({
        signatoryIndex: index,
        label: signatory.name || signatory.email || `Signatory ${index + 1}`,
        tier: hit.tier,
        rect: hit.rect,
      });
    }

    if (hit && page && containerRect && page.width > 0) {
      // Viewport rect → page-relative points. `page.left/top` and `page.width`
      // are already in the container's scaled pixel space, so dividing by
      // px-per-point removes the zoom without needing it passed in.
      const pxPerPt = page.width / page.widthPt;
      const width = Math.max(1, (hit.rect.right - hit.rect.left) / pxPerPt);
      const height = FORCED_ANCHOR_HEIGHT_PT;
      const x = (hit.rect.left - containerRect.left - page.left) / pxPerPt;
      const y =
        (hit.rect.top - containerRect.top - page.top) / pxPerPt -
        height * PREVIEW_ANCHOR_LIFT_RATIO;
      const clampedX = Math.max(0, Math.min(x, page.widthPt - width));
      const clampedY = Math.max(0, Math.min(y, page.heightPt - height));

      trace(hit.tier === 'index' && clampedY === y && clampedX === x, '[docx-seed] anchor', {
        signatory: signatory.id,
        tier: hit.tier,
        paragraphStored: anchor?.paragraphIndex ?? null,
        paragraphUsed: hit.paragraphIndex,
        page: page.pageNumber,
        box: { x: clampedX, y: clampedY, width, height },
        // A clamp firing means the section overflowed its nominal page box, so
        // the anchor mapped outside the page the overlay thinks it is drawing.
        clamped: clampedX !== x || clampedY !== y,
      });

      fields.push({
        id: sigFieldId(index),
        type: 'signature',
        signatoryIndex: index,
        pageNumber: page.pageNumber,
        x: clampedX,
        y: clampedY,
        width,
        height,
      });
      fields.push({
        id: dateFieldId(index),
        type: 'date',
        signatoryIndex: index,
        pageNumber: page.pageNumber,
        x: Math.max(0, Math.min(clampedX + width + GAP, page.widthPt - DATE_W)),
        y: clampedY,
        width: DATE_W,
        height: DATE_H,
      });
      return;
    }

    unanchored.push({
      signatoryIndex: index,
      label: signatory.name || signatory.email || `Signatory ${index + 1}`,
      reason: anchor?.originalText ? 'not-located' : 'no-anchor',
    });

    trace(!anchor?.originalText, '[docx-seed] default placement', {
      signatory: signatory.id,
      reason: !anchor?.originalText ? 'no anchor' : !hit ? 'not located' : 'no page',
    });

    // 3. Nothing to anchor to — a default box the user can drag.
    fields.push(
      ...defaultFieldPair(index, lastPage.pageNumber, {
        widthPt: lastPage.widthPt,
        heightPt: lastPage.heightPt,
      })
    );
  });

  // Page numbers are load-bearing: x/y are page-relative, so a page that renders
  // taller than its nominal box silently pushes everything below the boundary
  // past the page bottom. Report it whenever a placement had to be guessed.
  trace(unanchored.length === 0, '[docx-seed] page geometry', {
    pages: pages.length,
    overflowing: pages.filter((p) => p.measuredHeight > p.height + 1).length,
    detail: pages.map((p) => ({
      n: p.pageNumber,
      sizePt: `${Math.round(p.widthPt)}x${Math.round(p.heightPt)}`,
      nominalPx: Math.round(p.height),
      measuredPx: Math.round(p.measuredHeight),
    })),
  });

  return { fields, anchors, unanchored };
}
